import Image from "next/image";
import React from "react";

import {
  BUNDLE_PRODUCT_IMAGES,
  type BundleProductImage as BundleProductImageData,
} from "./images";
import type {
  BundleItem,
  BundleProductImageType,
} from "./types";
import { cn } from "@/lib/utils";

interface BundleProductImageProps {
  /** Array of bundle items, each containing a product image key */
  bundles: BundleItem[];
  /** Optional additional className */
  className?: string;
}

/** Extended image data with key for React rendering */
type ImageWithKey = BundleProductImageData & { key: BundleProductImageType };

/**
 * Renders a row of product images for bundle cards
 * Displays 2-3 product images (router, phone, tv) with equal-width tiles
 */
const BundleProductImage: React.FC<BundleProductImageProps> = ({
  bundles,
  className,
}) => {
  if (!bundles || bundles.length === 0) {
    return null;
  }

  // Get image data for each bundle item
  const images: ImageWithKey[] = bundles
    .map((bundle) => {
      const imageData = BUNDLE_PRODUCT_IMAGES[bundle.productImage];
      return imageData ? { ...imageData, key: bundle.productImage } : null;
    })
    .filter((item): item is ImageWithKey => item !== null);

  if (images.length === 0) {
    return null;
  }

  const isTwoImages = images.length === 2;

  return (
    <div
      className={cn(
        "bundle-product-images",
        "flex w-full items-center justify-center gap-1",
        isTwoImages && "max-w-70",
        className
      )}
    >
      {images.map((image) => (
        <div
          key={image.key}
          className={cn(
            "bundle-product-image",
            "flex-1 overflow-hidden rounded-lg bg-[rgba(246,246,249,0.3)] backdrop-blur-[7.5px]"
          )}
        >
          <div className="relative aspect-[300/240] w-full">
            <Image
              src={image.src}
              alt={image.alt}
              fill
              sizes="(min-width: 1280px) 120px, (min-width: 768px) 100px, 90px"
              className="object-contain"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default BundleProductImage;
